import { useEffect, useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const GREETING = "👋 Willkommen bei MainSpessartBlick! Wie kann ich Ihnen heute helfen?";
const VOICE_GREETING = "Hallo, ich bin Nic, der virtuelle Assistent von MainSpessartBlick. Schön, dass du da bist! Wie kann ich dir helfen?";
const CLOSED_W = 90, CLOSED_H = 90;
const GREETING_W = 300, GREETING_H = 210;
const OPEN_W = 380, OPEN_H = 640;
const LS_SESSION = "msb_nic_session";
const LS_HISTORY = "msb_nic_history";
const LS_GREETING = "msb_nic_greeting_shown";
const LS_VOICE_CALLS = "msb_nic_voice_calls";
const MAX_VOICE_CALLS = 3;

function postSize(mode) {
  const size = mode === "open" ? [OPEN_W, OPEN_H]
             : mode === "greeting" ? [GREETING_W, GREETING_H]
             : [CLOSED_W, CLOSED_H];
  window.parent?.postMessage({ type: "msb-nic-size", width: size[0], height: size[1] }, "*");
}

function cleanForSpeech(text) {
  if (!text) return "";
  return text
    .replace(/https?:\/\/\S+/g, "")
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, "")
    .replace(/[*_`#>]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function pickGermanVoice() {
  const voices = window.speechSynthesis?.getVoices?.() || [];
  const preferred = ["Anna", "Petra", "Google Deutsch", "Microsoft Katja", "Microsoft Hedda"];
  for (const name of preferred) {
    const v = voices.find(x => x.name?.includes(name));
    if (v) return v;
  }
  return voices.find(v => v.lang?.startsWith("de")) || null;
}

function fmtDateShort(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("de-DE", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" });
  } catch { return ""; }
}

export default function NICWidget() {
  const [open, setOpen] = useState(false);
  const [greetingVisible, setGreetingVisible] = useState(false);
  const [tab, setTab] = useState("chat");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => {
    let sid = localStorage.getItem(LS_SESSION);
    if (!sid) { sid = crypto.randomUUID(); localStorage.setItem(LS_SESSION, sid); }
    return sid;
  });

  const [voiceStatus, setVoiceStatus] = useState("idle");
  const [voiceMuted, setVoiceMuted] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(true);
  const [interimText, setInterimText] = useState("");
  const [voiceCalls, setVoiceCalls] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_VOICE_CALLS) || "[]"); } catch { return []; }
  });
  const [expandedCall, setExpandedCall] = useState(null);

  const listRef = useRef(null);
  const greetingTimerRef = useRef(null);
  const recRef = useRef(null);
  const voiceActiveRef = useRef(false);
  const mutedRef = useRef(false);
  const currentCallRef = useRef(null); // { id, started_at, messages: [] }

  useEffect(() => {
    postSize("closed");
    try {
      const hist = JSON.parse(localStorage.getItem(LS_HISTORY) || "null");
      if (Array.isArray(hist) && hist.length) setMessages(hist);
      else setMessages([{ role: "assistant", content: GREETING }]);
    } catch { setMessages([{ role: "assistant", content: GREETING }]); }

    if (localStorage.getItem(LS_GREETING) !== "1") {
      const showT = setTimeout(() => {
        setGreetingVisible(true);
        postSize("greeting");
        greetingTimerRef.current = setTimeout(() => {
          setGreetingVisible(false);
          try { localStorage.setItem(LS_GREETING, "1"); } catch {}
          postSize("closed");
        }, 12000);
      }, 1500);
      return () => { clearTimeout(showT); if (greetingTimerRef.current) clearTimeout(greetingTimerRef.current); };
    }
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = () => {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      try { localStorage.setItem(LS_HISTORY, JSON.stringify(messages.slice(-200))); } catch {}
    }
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => { mutedRef.current = voiceMuted; }, [voiceMuted]);

  const persistVoiceCalls = (arr) => {
    setVoiceCalls(arr);
    try { localStorage.setItem(LS_VOICE_CALLS, JSON.stringify(arr)); } catch {}
  };

  const dismissGreeting = (e) => {
    if (e) e.stopPropagation();
    if (greetingTimerRef.current) clearTimeout(greetingTimerRef.current);
    setGreetingVisible(false);
    try { localStorage.setItem(LS_GREETING, "1"); } catch {}
    postSize(open ? "open" : "closed");
  };

  const openChat = () => {
    if (greetingTimerRef.current) clearTimeout(greetingTimerRef.current);
    setGreetingVisible(false);
    try { localStorage.setItem(LS_GREETING, "1"); } catch {}
    setOpen(true);
    setTab("chat");
    postSize("open");
  };
  const closeChat = () => {
    stopVoiceSession();
    setOpen(false);
    postSize("closed");
  };

  const sendText = async (text, opts = {}) => {
    const clean = (text || "").trim();
    if (!clean || busy) return "";
    if (!opts.silent) setInput("");
    const newMessages = [...messages, { role: "user", content: clean }, { role: "assistant", content: "" }];
    setMessages(newMessages);
    // parallele Aufzeichnung in aktuellem Voice-Call
    if (currentCallRef.current) {
      currentCallRef.current.messages.push({ role: "user", content: clean, ts: new Date().toISOString() });
    }
    setBusy(true);
    let full = "";
    try {
      const resp = await fetch(`${API}/api/public/nic/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: clean, mode: opts.mode || "chat" }),
      });
      if (resp.status === 429) {
        const m1 = "Sie haben gerade viele Nachrichten geschrieben. Bitte einen Moment und gleich wieder versuchen.";
        setMessages(m => { const c = [...m]; c[c.length-1] = { role: "assistant", content: m1 }; return c; });
        return m1;
      }
      if (resp.status === 503) {
        const m2 = "Unser AI-Assistent macht gerade Pause. Bitte direkt melden: +49 176 25799945.";
        setMessages(m => { const c = [...m]; c[c.length-1] = { role: "assistant", content: m2 }; return c; });
        return m2;
      }
      if (!resp.ok || !resp.body) throw new Error("Netzwerkfehler");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let sentenceBuf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        for (const line of chunk.split("\n")) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          full += payload;
          if (opts.speak) {
            sentenceBuf += payload;
            const m = sentenceBuf.match(/^(.*?[.!?…\n])(.*)$/s);
            if (m) {
              const spoken = m[1];
              sentenceBuf = m[2];
              const cleanTxt = cleanForSpeech(spoken);
              if (cleanTxt) speak(cleanTxt);
            }
          }
          setMessages(mx => { const c = [...mx]; c[c.length-1] = { role: "assistant", content: full }; return c; });
        }
      }
      if (opts.speak) {
        const rest = cleanForSpeech(sentenceBuf);
        if (rest) speak(rest);
      }
      if (currentCallRef.current) {
        currentCallRef.current.messages.push({ role: "assistant", content: full, ts: new Date().toISOString() });
      }
      return full;
    } catch {
      const err = "Verbindung unterbrochen. Bitte gleich nochmal versuchen.";
      setMessages(mx => { const c = [...mx]; c[c.length-1] = { role: "assistant", content: err }; return c; });
      return err;
    } finally { setBusy(false); }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(input); } };

  const clearChatHistory = () => {
    if (!window.confirm("Möchtest du den gesamten Chat-Verlauf löschen?")) return;
    localStorage.removeItem(LS_HISTORY);
    setMessages([{ role: "assistant", content: GREETING }]);
  };
  const deleteChatMessage = (idx) => {
    setMessages(m => m.filter((_, i) => i !== idx));
  };

  const speak = (text) => {
    if (!window.speechSynthesis || !text) return;
    const utter = new SpeechSynthesisUtterance(text);
    const v = pickGermanVoice();
    if (v) utter.voice = v;
    utter.lang = "de-DE";
    utter.rate = 1.02;
    utter.pitch = 1.0;
    utter.volume = 1.0;
    utter.onstart = () => setVoiceStatus("speaking");
    utter.onend = () => {
      if (!window.speechSynthesis.speaking && voiceActiveRef.current && !mutedRef.current) {
        setVoiceStatus("listening");
        startRecognition();
      }
    };
    window.speechSynthesis.speak(utter);
  };

  const stopSpeaking = () => { try { window.speechSynthesis?.cancel(); } catch {} };

  const startRecognition = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setVoiceSupported(false); return; }
    if (recRef.current) { try { recRef.current.stop(); } catch {} }
    if (mutedRef.current) return;
    const rec = new SR();
    rec.lang = "de-DE";
    rec.interimResults = true;
    rec.continuous = false;
    rec.maxAlternatives = 1;
    let finalText = "";
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) finalText += r[0].transcript;
        else interim += r[0].transcript;
      }
      setInterimText(interim || finalText);
    };
    rec.onerror = (ev) => {
      if (ev.error === "not-allowed" || ev.error === "service-not-allowed") {
        setVoiceSupported(false); setVoiceStatus("idle");
      }
    };
    rec.onend = async () => {
      const said = (finalText || "").trim();
      setInterimText("");
      if (!voiceActiveRef.current || mutedRef.current) return;
      if (!said) { if (voiceActiveRef.current) startRecognition(); return; }
      setVoiceStatus("thinking");
      await sendText(said, { speak: true, mode: "voice", silent: true });
    };
    try { rec.start(); recRef.current = rec; } catch {}
  };

  const stopRecognition = () => {
    if (recRef.current) { try { recRef.current.stop(); } catch {} recRef.current = null; }
    setInterimText("");
  };

  const startVoiceSession = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setVoiceSupported(false); return; }
    voiceActiveRef.current = true;
    setVoiceStatus("speaking");
    setVoiceMuted(false);
    mutedRef.current = false;
    // Neuer Voice-Call-Datensatz
    currentCallRef.current = {
      id: crypto.randomUUID(),
      started_at: new Date().toISOString(),
      messages: [{ role: "assistant", content: VOICE_GREETING, ts: new Date().toISOString() }],
    };
    setMessages(m => [...m, { role: "assistant", content: VOICE_GREETING }]);
    setTimeout(() => speak(VOICE_GREETING), 250);
  };

  const stopVoiceSession = () => {
    voiceActiveRef.current = false;
    stopSpeaking();
    stopRecognition();
    setVoiceStatus("idle");
    // Voice-Call speichern (nur wenn Gast tatsächlich gesprochen hat)
    if (currentCallRef.current) {
      const call = currentCallRef.current;
      const hasUser = call.messages.some(m => m.role === "user");
      if (hasUser) {
        const next = [call, ...voiceCalls].slice(0, MAX_VOICE_CALLS);
        persistVoiceCalls(next);
      }
      currentCallRef.current = null;
    }
  };

  const toggleMute = () => {
    setVoiceMuted(prev => {
      const next = !prev;
      mutedRef.current = next;
      if (next) { stopRecognition(); setVoiceStatus("speaking"); }
      else if (voiceActiveRef.current && !window.speechSynthesis?.speaking) {
        setVoiceStatus("listening"); startRecognition();
      }
      return next;
    });
  };

  const endCall = () => { stopVoiceSession(); setTab("chat"); };

  useEffect(() => {
    if (tab === "voice") startVoiceSession();
    else stopVoiceSession();
    return () => { if (tab !== "voice") stopVoiceSession(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const deleteVoiceCall = (id) => {
    const next = voiceCalls.filter(c => c.id !== id);
    persistVoiceCalls(next);
    if (expandedCall === id) setExpandedCall(null);
  };

  const keyframes = `
    @keyframes msb-slide-in {
      0%   { opacity: 0; transform: translateY(6px) scale(.92); }
      60%  { opacity: 1; transform: translateY(0) scale(1.02); }
      100% { opacity: 1; transform: translateY(0) scale(1); }
    }
    @keyframes msb-pulse {
      0%,100% { box-shadow: 0 6px 20px rgba(45,90,61,.45), 0 0 0 0 rgba(37,211,102,.55); }
      50%     { box-shadow: 0 6px 20px rgba(45,90,61,.45), 0 0 0 10px rgba(37,211,102,0); }
    }
    @keyframes msb-ring {
      0%   { transform: scale(1);   opacity: .7; }
      100% { transform: scale(1.6); opacity: 0;  }
    }
    @keyframes msb-dot {
      0%,100% { opacity:.3; transform: translateY(0); }
      50%     { opacity:1;  transform: translateY(-3px); }
    }
    .msb-msg-row { position:relative; }
    .msb-msg-row .msb-del { opacity:0; transition:opacity .15s; }
    .msb-msg-row:hover .msb-del { opacity:1; }
  `;

  if (!open) {
    return (
      <div style={{margin:0, padding:0, background:"transparent", width: greetingVisible ? GREETING_W : CLOSED_W, height: greetingVisible ? GREETING_H : CLOSED_H, overflow:"hidden", fontFamily:"'Segoe UI',Arial,Helvetica,sans-serif"}}>
        <style>{keyframes}</style>
        {greetingVisible && (
          <div role="button" tabIndex={0} onClick={openChat}
            onKeyDown={e => (e.key === "Enter" || e.key === " ") && openChat()}
            data-testid="nic-greeting"
            style={{position:"absolute", right: 14, bottom: CLOSED_H + 6, width: 250, background:"#fff", borderRadius: 16, borderBottomRightRadius: 4, padding:"12px 30px 12px 14px", boxShadow:"0 10px 30px rgba(0,0,0,.22)", fontSize:13.5, lineHeight:1.45, color:"#1f2937", cursor:"pointer", animation:"msb-slide-in .45s ease-out", userSelect:"none"}}>
            <button onClick={dismissGreeting} aria-label="Begrüßung schließen" data-testid="nic-greeting-close"
              style={{position:"absolute", top:4, right:6, background:"transparent", border:0, cursor:"pointer", color:"#94a3b8", fontSize:18, lineHeight:1, padding:4, borderRadius:6}}
              onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>×</button>
            <div style={{fontSize:15, fontWeight:600, marginBottom:2}}>👋 Willkommen bei MainSpessartBlick!</div>
            <div style={{color:"#64748b", fontSize:13}}>Wie kann ich Ihnen heute helfen?</div>
            <div style={{position:"absolute", right: 18, bottom: -8, width: 0, height: 0, borderLeft:"8px solid transparent", borderRight:"8px solid transparent", borderTop:"10px solid #fff", filter:"drop-shadow(0 2px 2px rgba(0,0,0,.06))"}}/>
          </div>
        )}
        <button onClick={openChat} aria-label="Chat mit NIC öffnen" data-testid="nic-bubble"
          style={{position:"absolute", right:10, bottom:10, width:70, height:70, borderRadius:"50%", background:"linear-gradient(135deg,#2d5a3d 0%,#3f7a54 100%)", border:"3px solid #fff", boxShadow:"0 6px 20px rgba(45,90,61,.45)", cursor:"pointer", padding:0, display:"grid", placeItems:"center", transition:"transform .2s ease", animation: greetingVisible ? "msb-pulse 1.8s ease-in-out infinite" : "none"}}
          onMouseEnter={e => e.currentTarget.style.transform = "scale(1.08)"}
          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
          <img src={`${API}/msb-logo.jpg`} alt="MSB" style={{width:56, height:56, borderRadius:"50%", objectFit:"cover", display:"block"}} />
          <span style={{position:"absolute", top:-2, right:-2, width:20, height:20, borderRadius:"50%", background:"#25D366", border:"2px solid #fff", display:"grid", placeItems:"center", fontSize:12, color:"#fff", fontWeight:800}}>💬</span>
        </button>
      </div>
    );
  }

  return (
    <div style={{margin:0, padding:0, background:"transparent", width:OPEN_W, height:OPEN_H, fontFamily:"'Segoe UI',Arial,Helvetica,sans-serif"}}>
      <style>{keyframes}</style>
      <div style={{position:"absolute", right:10, bottom:10, width:OPEN_W - 20, height:OPEN_H - 20, background:"#fff", borderRadius:16, overflow:"hidden", boxShadow:"0 12px 40px rgba(0,0,0,.25)", display:"flex", flexDirection:"column", animation:"msb-slide-in .3s ease-out"}}>

        <div style={{background:"linear-gradient(135deg,#2d5a3d 0%,#3f7a54 100%)", color:"#fff", padding:"14px 16px", display:"flex", alignItems:"center", gap:12}}>
          <img src={`${API}/msb-logo.jpg`} alt="MSB" style={{width:40, height:40, borderRadius:"50%", objectFit:"cover", background:"#fff"}}/>
          <div style={{flex:1}}>
            <div style={{fontWeight:800, fontSize:15, lineHeight:1.2}}>NIC · MainSpessartBlick</div>
            <div style={{fontSize:11, opacity:.85, display:"flex", alignItems:"center", gap:6}}>
              <span style={{width:8, height:8, borderRadius:"50%", background:"#25D366", display:"inline-block"}}></span>
              online · antwortet meist in Sekunden
            </div>
          </div>
          {tab === "chat" && (
            <button onClick={clearChatHistory} title="Chat-Verlauf löschen" data-testid="nic-reset" style={{background:"transparent", border:0, color:"#fff", cursor:"pointer", padding:6, opacity:.7, fontSize:16}}>↻</button>
          )}
          <button onClick={closeChat} aria-label="Chat schließen" data-testid="nic-close" style={{background:"transparent", border:0, color:"#fff", cursor:"pointer", padding:6, fontSize:22, lineHeight:1}}>×</button>
        </div>

        {tab === "chat" && (
          <div ref={listRef} style={{flex:1, overflowY:"auto", padding:"14px 12px", background:"#f5f7f6", display:"flex", flexDirection:"column", gap:8}}>
            {messages.map((m, i) => (
              <div key={i} className="msb-msg-row" style={{display:"flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", gap:4, alignItems:"flex-start"}}>
                {m.role === "user" && (
                  <button className="msb-del" onClick={() => deleteChatMessage(i)} title="Nachricht löschen"
                    style={{background:"transparent", border:0, cursor:"pointer", color:"#94a3b8", padding:"6px 4px 0 4px", fontSize:14}}>🗑️</button>
                )}
                <div style={{maxWidth:"82%", background: m.role === "user" ? "#2d5a3d" : "#fff", color: m.role === "user" ? "#fff" : "#1f2937", padding:"9px 13px", borderRadius:14, borderBottomRightRadius: m.role === "user" ? 4 : 14, borderBottomLeftRadius: m.role === "user" ? 14 : 4, fontSize:14, lineHeight:1.5, whiteSpace:"pre-wrap", wordBreak:"break-word", boxShadow: m.role === "user" ? "none" : "0 1px 2px rgba(0,0,0,.06)"}}
                  dangerouslySetInnerHTML={{__html: linkify(m.content) || (busy && i === messages.length-1 ? "…" : "")}}/>
                {m.role === "assistant" && (
                  <button className="msb-del" onClick={() => deleteChatMessage(i)} title="Nachricht löschen"
                    style={{background:"transparent", border:0, cursor:"pointer", color:"#94a3b8", padding:"6px 4px 0 4px", fontSize:14}}>🗑️</button>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "voice" && (
          <div style={{flex:1, background:"#fff", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"20px 20px", position:"relative"}}>
            {!voiceSupported ? (
              <div style={{textAlign:"center", color:"#64748b", fontSize:14, lineHeight:1.5}}>
                Sprach-Modus wird von diesem Browser nicht unterstützt.<br/>
                Bitte nutze <strong>Chrome, Edge oder Safari</strong> — oder wechsel zum Chat.
              </div>
            ) : (
              <>
                <div style={{position:"relative", width:180, height:180, display:"grid", placeItems:"center", marginBottom:16}}>
                  {voiceStatus === "listening" && !voiceMuted && (
                    <>
                      <span style={{position:"absolute", inset:20, borderRadius:"50%", border:"3px solid #2d5a3d", animation:"msb-ring 1.6s ease-out infinite"}}/>
                      <span style={{position:"absolute", inset:20, borderRadius:"50%", border:"3px solid #2d5a3d", animation:"msb-ring 1.6s ease-out infinite .5s"}}/>
                    </>
                  )}
                  {voiceStatus === "speaking" && (
                    <span style={{position:"absolute", inset:20, borderRadius:"50%", border:"3px solid #3f7a54", animation:"msb-pulse 1.4s ease-in-out infinite"}}/>
                  )}
                  <img src={`${API}/msb-logo.jpg`} alt="NIC" style={{width:140, height:140, borderRadius:"50%", objectFit:"cover", background:"#fff", boxShadow:"0 4px 20px rgba(0,0,0,.12)"}}/>
                </div>

                <div style={{fontSize:22, fontWeight:800, color:"#1f2937", display:"flex", alignItems:"center", gap:8}}>
                  Nic
                  <span style={{fontSize:11, fontWeight:800, background:"#1f2937", color:"#fff", padding:"2px 6px", borderRadius:4}}>AI</span>
                </div>
                <div style={{fontSize:14, color:"#64748b", marginTop:2}}>Sprach-Assistent</div>

                <div style={{marginTop:14, color:"#64748b", fontSize:14, minHeight:22, display:"flex", alignItems:"center", gap:6}}>
                  {voiceMuted ? <span>Mikrofon stummgeschaltet</span>
                    : voiceStatus === "listening" ? (
                      <>Hört zu
                        <span style={{display:"inline-flex", gap:2, marginLeft:4}}>
                          <span style={{width:4, height:4, borderRadius:"50%", background:"#64748b", animation:"msb-dot 1s infinite"}}/>
                          <span style={{width:4, height:4, borderRadius:"50%", background:"#64748b", animation:"msb-dot 1s infinite .2s"}}/>
                          <span style={{width:4, height:4, borderRadius:"50%", background:"#64748b", animation:"msb-dot 1s infinite .4s"}}/>
                        </span>
                      </>
                    ) : voiceStatus === "thinking" ? <span>Denke nach…</span>
                    : voiceStatus === "speaking" ? <span>Spricht…</span>
                    : <span>Bereit</span>}
                </div>

                {interimText && (
                  <div style={{marginTop:10, fontSize:13, color:"#94a3b8", fontStyle:"italic", textAlign:"center", maxWidth:280, minHeight:18}}>
                    „{interimText}"
                  </div>
                )}

                <div style={{marginTop:26, display:"flex", gap:36, alignItems:"flex-start"}}>
                  <button onClick={toggleMute} data-testid="nic-voice-mute" aria-label="Stummschalten"
                    style={{background:"transparent", border:0, cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:6, color:"#64748b", fontSize:12}}>
                    <span style={{width:56, height:56, borderRadius:"50%", background: voiceMuted ? "#fca5a5" : "#e5e7eb", display:"grid", placeItems:"center", boxShadow:"0 2px 8px rgba(0,0,0,.08)"}}>
                      {voiceMuted ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7f1d1d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                      )}
                    </span>
                    <span>{voiceMuted ? "Aufheben" : "Stummschalten"}</span>
                  </button>

                  <button onClick={endCall} data-testid="nic-voice-end" aria-label="Anruf beenden"
                    style={{background:"transparent", border:0, cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:6, color:"#64748b", fontSize:12}}>
                    <span style={{width:56, height:56, borderRadius:"50%", background:"#ef4444", display:"grid", placeItems:"center", boxShadow:"0 4px 12px rgba(239,68,68,.4)"}}>
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </span>
                    <span>Anruf beenden</span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "history" && (
          <div style={{flex:1, overflowY:"auto", background:"#f5f7f6", padding:"12px"}}>
            {/* Sprachanrufe */}
            <div style={{fontSize:11, fontWeight:800, textTransform:"uppercase", color:"#64748b", letterSpacing:.4, marginBottom:6}}>Sprachanrufe · max. 3</div>
            {voiceCalls.length === 0 && (
              <div style={{background:"#fff", borderRadius:10, padding:"12px", fontSize:13, color:"#94a3b8", marginBottom:14, textAlign:"center"}}>Noch keine Sprachanrufe.</div>
            )}
            {voiceCalls.map(call => {
              const first = call.messages.find(m => m.role === "user");
              const preview = first?.content?.slice(0, 60) || "(kein Text erfasst)";
              const isOpen = expandedCall === call.id;
              return (
                <div key={call.id} style={{background:"#fff", borderRadius:10, padding:"10px 12px", marginBottom:8, boxShadow:"0 1px 2px rgba(0,0,0,.04)"}}>
                  <div style={{display:"flex", alignItems:"center", gap:8}}>
                    <div style={{width:32, height:32, borderRadius:"50%", background:"#e6f0e9", display:"grid", placeItems:"center", flexShrink:0}}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2d5a3d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12h2M7 8v8M11 4v16M15 8v8M19 12h2"/></svg>
                    </div>
                    <div style={{flex:1, minWidth:0, cursor:"pointer"}} onClick={() => setExpandedCall(isOpen ? null : call.id)}>
                      <div style={{fontSize:13, fontWeight:600, color:"#1f2937", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>
                        „{preview}{first?.content?.length > 60 ? "…" : ""}"
                      </div>
                      <div style={{fontSize:11, color:"#94a3b8"}}>{fmtDateShort(call.started_at)} · {call.messages.length} Nachrichten</div>
                    </div>
                    <button onClick={() => deleteVoiceCall(call.id)} title="Anruf löschen" data-testid={`nic-voice-del-${call.id}`}
                      style={{background:"transparent", border:0, cursor:"pointer", color:"#ef4444", fontSize:15, padding:6}}>🗑️</button>
                  </div>
                  {isOpen && (
                    <div style={{marginTop:8, paddingTop:8, borderTop:"1px solid #f1f5f4", display:"flex", flexDirection:"column", gap:6, maxHeight:180, overflowY:"auto"}}>
                      {call.messages.map((m, i) => (
                        <div key={i} style={{fontSize:12, lineHeight:1.4}}>
                          <span style={{fontWeight:700, color: m.role === "user" ? "#2d5a3d" : "#64748b"}}>{m.role === "user" ? "Gast" : "NIC"}: </span>
                          <span style={{color:"#374151"}}>{m.content}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Chat-Zusammenfassung */}
            <div style={{fontSize:11, fontWeight:800, textTransform:"uppercase", color:"#64748b", letterSpacing:.4, marginTop:14, marginBottom:6, display:"flex", justifyContent:"space-between", alignItems:"center"}}>
              <span>Chat-Verlauf · {messages.filter(m => m.role === "user").length} Nachrichten</span>
              {messages.length > 1 && (
                <button onClick={clearChatHistory} style={{background:"transparent", border:0, color:"#ef4444", fontSize:11, fontWeight:700, cursor:"pointer", textTransform:"none"}}>alle löschen</button>
              )}
            </div>
            {messages.length <= 1 ? (
              <div style={{background:"#fff", borderRadius:10, padding:"12px", fontSize:13, color:"#94a3b8", textAlign:"center"}}>Noch keine Chat-Nachrichten.</div>
            ) : (
              <div style={{background:"#fff", borderRadius:10, padding:"10px 12px", display:"flex", flexDirection:"column", gap:6, maxHeight:200, overflowY:"auto"}}>
                {messages.map((m, i) => (
                  <div key={i} style={{fontSize:12, lineHeight:1.4, display:"flex", gap:6, alignItems:"flex-start"}}>
                    <div style={{flex:1}}>
                      <span style={{fontWeight:700, color: m.role === "user" ? "#2d5a3d" : "#64748b"}}>{m.role === "user" ? "Gast" : "NIC"}: </span>
                      <span style={{color:"#374151"}}>{m.content}</span>
                    </div>
                    <button onClick={() => deleteChatMessage(i)} title="Löschen"
                      style={{background:"transparent", border:0, cursor:"pointer", color:"#94a3b8", fontSize:12, padding:"0 4px"}}>🗑️</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "chat" && (
          <div style={{borderTop:"1px solid #e5e7eb", background:"#fff", padding:"10px 12px", display:"flex", gap:8, alignItems:"flex-end"}}>
            <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={onKey}
              placeholder="Nachricht schreiben…" rows={1} data-testid="nic-input"
              style={{flex:1, resize:"none", border:"1px solid #d1d5db", borderRadius:12, padding:"9px 12px", fontSize:14, fontFamily:"inherit", outline:"none", maxHeight:100, background:"#fafafa"}}/>
            <button onClick={() => sendText(input)} disabled={busy || !input.trim()} data-testid="nic-send"
              style={{background: busy || !input.trim() ? "#94a3b8" : "#2d5a3d", color:"#fff", border:0, borderRadius:12, padding:"10px 14px", cursor: busy || !input.trim() ? "default" : "pointer", fontSize:14, fontWeight:700, minWidth:56}}>
              {busy ? "…" : "➤"}
            </button>
          </div>
        )}

        <div style={{borderTop:"1px solid #e5e7eb", background:"#fff", display:"flex", justifyContent:"space-around", padding:"6px 0"}}>
          <TabButton active={tab==="chat"} onClick={() => setTab("chat")} testid="nic-tab-chat"
            icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>}
            label="Chat"/>
          <TabButton active={tab==="voice"} onClick={() => setTab("voice")} testid="nic-tab-voice"
            icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12h2M7 8v8M11 4v16M15 8v8M19 12h2"/></svg>}
            label="Sprache"/>
          <TabButton active={tab==="history"} onClick={() => setTab("history")} testid="nic-tab-history"
            icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>}
            label="Verlauf"/>
        </div>

        <div style={{fontSize:10, color:"#94a3b8", textAlign:"center", padding:"4px 0 6px 0", background:"#fff"}}>
          KI-Assistent · Bei komplexen Anliegen: <a href="tel:+4917625799945" style={{color:"#2d5a3d",textDecoration:"none",fontWeight:600}}>+49 176 25799945</a>
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      style={{
        flex:1, background:"transparent", border:0, cursor:"pointer",
        display:"flex", flexDirection:"column", alignItems:"center", gap:2,
        padding:"6px 4px",
        color: active ? "#2d5a3d" : "#94a3b8",
        fontWeight: active ? 700 : 500,
        fontSize:11,
        borderTop: active ? "2px solid #2d5a3d" : "2px solid transparent",
        marginTop: -6, paddingTop: 8,
      }}>
      {icon}
      <span>{label}</span>
    </button>
  );
}

function linkify(text) {
  if (!text) return "";
  const esc = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc.replace(/(https?:\/\/[^\s<]+)/g, url =>
    `<a href="${url}" target="_blank" rel="noopener" style="color:#2d5a3d;font-weight:600;text-decoration:underline">${url}</a>`
  ).replace(/\n/g, "<br>");
}
